import React, { useMemo, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { Employee, ProJob } from '../../types/pro';
import { JOB_STATUS, addDays, formatTime, isSameDay, startOfWeek } from '../../utils/pro';
import { PageHeader } from '../../components/pro/PageHeader';
interface ProCalendarProps {
  jobs: ProJob[];
  employees: Employee[];
  onOpenJob: (jobId: string) => void;
  onUpdateJob: (jobId: string, changes: Partial<ProJob>) => void;
  onCreateAt: (start: Date, employeeId: string) => void;
}
type Mode = 'dag' | 'uge' | 'maaned';
const START_HOUR = 6;
const END_HOUR = 18;
export function ProCalendar({
  jobs,
  employees,
  onOpenJob,
  onUpdateJob,
  onCreateAt
}: ProCalendarProps) {
  const [mode, setMode] = useState<Mode>('uge');
  const [anchor, setAnchor] = useState(() => new Date());
  const [dragId, setDragId] = useState<string | null>(null);
  const days = useMemo(() => {
    if (mode === 'dag') return [anchor];
    if (mode === 'uge') {
      const monday = startOfWeek(anchor);
      return Array.from({
        length: 5
      }, (_, index) => addDays(monday, index));
    }
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const start = startOfWeek(first);
    return Array.from({
      length: 35
    }, (_, index) => addDays(start, index));
  }, [mode, anchor]);
  const step = (direction: number) => {
    if (mode === 'dag') setAnchor(addDays(anchor, direction));else if (mode === 'uge') setAnchor(addDays(anchor, direction * 7));else setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + direction, 1));
  };
  const jobsFor = (date: Date, employeeId?: string) => jobs.filter(job => {
    if (!job.start) return false;
    if (!isSameDay(new Date(job.start), date)) return false;
    if (employeeId) return job.employeeIds.includes(employeeId);
    return true;
  });
  const drop = (date: Date, hour: number, employeeId: string) => {
    const job = jobs.find(j => j.id === dragId);
    if (!job?.id) return;
    const start = new Date(date);
    start.setHours(hour, 0, 0, 0);
    onUpdateJob(job.id, {
      start: start.toISOString(),
      employeeIds: [employeeId],
      status: job.status === 'ikke-planlagt' ? 'planlagt' : job.status
    });
    setDragId(null);
  };
  const label = mode === 'maaned' ? anchor.toLocaleDateString('da-DK', {
    month: 'long',
    year: 'numeric'
  }) : mode === 'dag' ? anchor.toLocaleDateString('da-DK', {
    weekday: 'long',
    day: 'numeric',
    month: 'long'
  }) : `Uge ${Math.ceil(((startOfWeek(anchor).getTime() - new Date(anchor.getFullYear(), 0, 1).getTime()) / 86400000 + 1) / 7)} · ${startOfWeek(anchor).toLocaleDateString('da-DK', {
    day: 'numeric',
    month: 'short'
  })}`;
  const hours = Array.from({
    length: END_HOUR - START_HOUR
  }, (_, index) => START_HOUR + index);
  return <div data-id="element-984" data-mp="3w6c5ngwiwn5">
      <PageHeader title="Kalender" subtitle="Hvem er booket, og hvem har hul" actions={<div className="flex flex-wrap items-center gap-2" data-id="element-986" data-mp="3v7y04wcyl7j9">
            <div className="flex items-center gap-1 rounded-lg border border-stone-200 bg-white p-1" data-id="element-987" data-mp="3r9iccq91zv4s">
              <button onClick={() => step(-1)} aria-label="Forrige" className="rounded-md p-1.5 text-stone-500 transition-colors hover:bg-stone-100" data-id="element-988" data-mp="3manlr24uh2a4">
                <ChevronLeftIcon className="h-4 w-4" data-id="element-989" data-mp="it5iz93xilgk" />
              </button>
              <button onClick={() => setAnchor(new Date())} className="rounded-md px-2.5 py-1 text-xs font-semibold text-stone-700 transition-colors hover:bg-stone-100" data-id="element-990" data-mp="dsht4qrfsanx">
                I dag
              </button>
              <button onClick={() => step(1)} aria-label="Næste" className="rounded-md p-1.5 text-stone-500 transition-colors hover:bg-stone-100" data-id="element-991" data-mp="2tkowb5mqn34x">
                <ChevronRightIcon className="h-4 w-4" data-id="element-992" data-mp="q38rv2vw35bs" />
              </button>
            </div>
            <div role="group" aria-label="Visning" className="flex rounded-lg border border-stone-200 bg-white p-1" data-id="element-993" data-mp="cbkhp557aqxg">
              {(['dag', 'uge', 'maaned'] as Mode[]).map(option => <button key={option} onClick={() => setMode(option)} aria-pressed={mode === option} className={`rounded-md px-3 py-1.5 text-xs font-medium capitalize transition-colors ${mode === option ? 'bg-stone-900 text-white' : 'text-stone-500 hover:text-stone-900'}`} data-id="element-994" data-mp="150hoab01uzad">
                  {option === 'maaned' ? 'Måned' : option}
                </button>)}
            </div>
          </div>} data-id="element-985" data-mp="3wxzkb0cho2c" />

      <p className="mb-3 text-sm font-semibold capitalize text-stone-700" data-id="element-995" data-mp="3d6a5ggvekjnh">
        {label}
      </p>

      {mode === 'maaned' ? <div className="overflow-hidden rounded-xl border border-stone-200 bg-white" data-id="element-996" data-mp="38aup9i9xabnw">
          <div className="grid grid-cols-7 border-b border-stone-200 bg-stone-50" data-id="element-997" data-mp="3m66w2o1crsl6">
            {['Man', 'Tir', 'Ons', 'Tor', 'Fre', 'Lør', 'Søn'].map(name => <div key={name} className="px-2 py-2 text-center text-[11px] font-semibold uppercase tracking-wide text-stone-500" data-id="element-998" data-mp="2tkngju9pw70e">
                {name}
              </div>)}
          </div>
          <div className="grid grid-cols-7" data-id="element-999" data-mp="33b9tthtyolfx">
            {days.map(date => {
          const dayJobs = jobsFor(date);
          const isToday = isSameDay(date, new Date());
          const inMonth = date.getMonth() === anchor.getMonth();
          return <div key={date.toISOString()} className={`min-h-[92px] border-b border-r border-stone-100 p-1.5 ${inMonth ? '' : 'bg-stone-50/60'}`} data-id="element-1000" data-mp="25triwgikyhl8">
                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-xs font-semibold ${isToday ? 'bg-brand-600 text-white' : inMonth ? 'text-stone-700' : 'text-stone-300'}`} data-id="element-1001" data-mp="jg6b48r0bz74">
                    {date.getDate()}
                  </span>
                  <ul className="mt-1 space-y-1" data-id="element-1002" data-mp="ox200l5fcg2">
                    {dayJobs.slice(0, 3).map(job => {
                const employee = employees.find(e => job.employeeIds.includes(e.id ?? ''));
                return <li key={job.id} data-id="element-1003" data-mp="1g5dzdoj0gmeq">
                          <button onClick={() => job.id && onOpenJob(job.id)} className="block w-full truncate rounded px-1 py-0.5 text-left text-[11px] font-medium text-white" style={{
                    backgroundColor: employee?.color ?? '#78716C'
                  }} data-id="element-1004" data-mp="30ozsxzsgiigi">
                            {formatTime(job.start ?? '')} {job.title}
                          </button>
                        </li>;
              })}
                    {dayJobs.length > 3 && <li className="px-1 text-[10px] text-stone-400" data-id="element-1005" data-mp="28yv3hmpub58f">
                        +{dayJobs.length - 3} mere
                      </li>}
                  </ul>
                </div>;
        })}
          </div>
        </div> : (/* Employees as rows — the point is seeing who is free */
    <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white" data-id="element-1006" data-mp="25ax520o4zoa">
          <div className="min-w-[720px]" data-id="element-1007" data-mp="3787b5nl0zqaz">
            {days.map(date => <div key={date.toISOString()} className="border-b border-stone-200 last:border-b-0" data-id="element-1008" data-mp="3c579tdfjpqs2">
                <div className={`flex items-center gap-2 px-4 py-2 ${isSameDay(date, new Date()) ? 'bg-brand-50' : 'bg-stone-50'}`} data-id="element-1009" data-mp="32gpal0mwzrqj">
                  <h2 className="text-sm font-semibold capitalize text-stone-800" data-id="element-1010" data-mp="2si9lf37g6sjo">
                    {date.toLocaleDateString('da-DK', {
                weekday: 'long',
                day: 'numeric',
                month: 'short'
              })}
                  </h2>
                  {isSameDay(date, new Date()) && <span className="rounded-full bg-brand-600 px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-id="element-1011" data-mp="cs6jeyinukz9">
                      I dag
                    </span>}
                </div>

                <div className="flex border-b border-stone-100 bg-white" data-id="element-1012" data-mp="226ekh1v2hpfu">
                  <div className="w-32 flex-shrink-0" data-id="element-1013" data-mp="1xaz6m9x27tjf" />
                  {hours.map(hour => <div key={hour} className="flex-1 border-l border-stone-100 py-1 text-center text-[10px] font-medium text-stone-400" data-id="element-1014" data-mp="2bzvcq1fk2whe">
                      {hour}
                    </div>)}
                </div>

                {employees.map(employee => {
            const employeeJobs = jobsFor(date, employee.id);
            return <div key={employee.id} className="flex border-b border-stone-100 last:border-b-0" data-id="element-1015" data-mp="2py3dkkfpqhxu">
                      <div className="flex w-32 flex-shrink-0 items-center gap-2 px-3 py-2" data-id="element-1016" data-mp="10jf1kkb1b20c">
                        <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{
                  backgroundColor: employee.color
                }} data-id="element-1017" data-mp="vlwtew4ucqte" />
                        <span className="truncate text-xs font-medium text-stone-700" data-id="element-1018" data-mp="rhawhyuyrpgs">
                          {employee.name.split(' ')[0]}
                        </span>
                      </div>

                      <div className="relative flex flex-1" data-id="element-1019" data-mp="hmiywv1dt2m0">
                        {hours.map(hour => <button key={hour} onClick={() => {
                  const start = new Date(date);
                  start.setHours(hour, 0, 0, 0);
                  onCreateAt(start, employee.id ?? '');
                }} onDragOver={e => e.preventDefault()} onDrop={() => drop(date, hour, employee.id ?? '')} aria-label={`Ny aftale ${hour}:00 for ${employee.name}`} className="min-h-[44px] flex-1 border-l border-stone-100 transition-colors hover:bg-brand-50/50" data-id="element-1020" data-mp="1fa16w3n7y0oy" />)}

                        {employeeJobs.map(job => {
                  const start = new Date(job.start ?? '');
                  const offset = start.getHours() + start.getMinutes() / 60 - START_HOUR;
                  const span = END_HOUR - START_HOUR;
                  if (offset < 0 || offset >= span) return null;
                  return <button key={job.id} draggable onDragStart={() => setDragId(job.id ?? null)} onDragEnd={() => setDragId(null)} onClick={() => job.id && onOpenJob(job.id)} className={`absolute inset-y-1 overflow-hidden rounded px-2 py-1 text-left text-white transition-opacity ${dragId === job.id ? 'opacity-50' : ''}`} style={{
                    left: `${offset / span * 100}%`,
                    width: `${Math.min(job.durationHours / span * 100, 100 - offset / span * 100)}%`,
                    backgroundColor: employee.color
                  }} data-id="element-1021" data-mp="2y634wyt7y7pu">
                              <span className="block truncate text-[11px] font-semibold" data-id="element-1022" data-mp="1grji4nxkod9d">
                                {job.title}
                              </span>
                              <span className="block truncate text-[10px] opacity-80" data-id="element-1023" data-mp="1buavob5e5n2b">
                                {job.customerName}
                              </span>
                            </button>;
                })}
                      </div>
                    </div>;
          })}
              </div>)}
          </div>
        </div>)}

      <p className="mt-3 text-xs text-stone-400" data-id="element-1024" data-mp="3imqqlqaobrgl">
        Træk en blok til en anden medarbejder eller et andet tidspunkt. Klik på
        et tomt felt for at oprette en aftale.
      </p>
    </div>;
}